import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, Search, Users, X, Check, AlertTriangle } from "lucide-react";

import { api, formatApiError } from "@/lib/api";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

// FIELD-001C1B · Canonical resource-type catalogue (mirrors the
// EngineerForm constant + backend `RESOURCE_TYPES`). Kept small on
// purpose — the picker uses it AND accepts any legacy free-text
// value already present on the roster so historical data still loads.
const RESOURCE_TYPES = [
    "Electrician",
    "Panel Installer",
    "Scaffolder",
    "Groundworks Contractor",
    "General / Unspecified",
];

const KIND_LABEL = {
    internal_person: "Internal",
    external_individual: "External · Individual",
    external_organisation: "External · Organisation",
};

/**
 * FIELD-001B / FIELD-001C2A · Operations-only canonical task assignment dialog.
 *
 * Backs the per-task assignee control on OperationsProjectDetail. Multi-select
 * with an inline search, clear-all affordance, a required-resource-type
 * selector, and a Save button that calls `PATCH
 * /api/projects/{project_id}/tasks/{task_id}/assignees` — never touches
 * task status or the WF-001A1 dependency graph. Cancel closes the dialog
 * without a mutation. A failed save preserves the prior assignment and
 * surfaces the API error.
 *
 * FIELD-001C2A · When a required resource type is set:
 *   • Resources whose `resourceType` does NOT match are dimmed and
 *     disabled — Operations can still see them (never silently hidden)
 *     but cannot newly assign them.
 *   • The server rejects mismatched assignees with a 409; the UI
 *     surfaces the message verbatim and preserves the pending state.
 *   • Existing assignees that no longer match a newly-selected
 *     requirement are flagged inline so Operations can resolve them
 *     before saving (never silently reassigned).
 */
export default function TaskAssigneesDialog({
    projectId, task, currentAssigneeIds = [],
    currentRequirement = null,
    onClose, onSaved,
}) {
    const [engineers, setEngineers] = useState([]);
    // FIELD-RESOURCE-ASSIGNMENT-CORRECTION · Archived + missing engineers are
    // fetched separately so the current-assignee chips can label
    // "James Carter (archived)" or "Unknown resource (id: xxx8)" without
    // ever leaking a raw UUID or triggering a server error on save.
    const [archivedById, setArchivedById] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [q, setQ] = useState("");
    const [selected, setSelected] = useState(() => new Set(currentAssigneeIds));
    const [error, setError] = useState("");
    const [requirement, setRequirement] = useState(currentRequirement || "");
    // FIELD-001C2A · Tracks whether the operator has touched the
    // requirement input in this dialog session. Only send the field
    // over the wire when the user changed it — the backend preserves
    // the prior value when the field is omitted.
    const [requirementDirty, setRequirementDirty] = useState(false);

    useEffect(() => {
        setSelected(new Set(currentAssigneeIds));
        setRequirement(currentRequirement || "");
        setRequirementDirty(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [task?.id]);

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                // Selectable roster — active only.
                const rows = await api.listEngineers({ includeArchived: false });
                setEngineers(rows || []);
                // FIELD-RESOURCE-ASSIGNMENT-CORRECTION · Best-effort load of
                // archived rows so current stale assignees resolve to a
                // human label. Missing ids (truly deleted) fall through to
                // the "Unknown resource" branch below.
                try {
                    const all = await api.listEngineers({ includeArchived: true });
                    const map = {};
                    for (const e of (all || [])) {
                        if (e && e.active === false) map[e.id] = e;
                    }
                    setArchivedById(map);
                } catch { /* non-fatal — label falls back to id */ }
            } catch (e) {
                setError(formatApiError(e?.response?.data?.detail) || "Could not load resources");
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const filtered = useMemo(() => {
        const needle = q.trim().toLowerCase();
        if (!needle) return engineers;
        return engineers.filter((e) => {
            const bag = `${e.displayName || ""} ${e.organisationName || ""} ${e.resourceType || e.trade || ""} ${e.role || ""} ${e.email || ""}`.toLowerCase();
            return bag.includes(needle);
        });
    }, [engineers, q]);

    const toggle = (id, disabled) => {
        if (disabled) return;
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id); else next.add(id);
            return next;
        });
    };

    const clearAll = () => setSelected(new Set());

    // Discover free-text resourceType values already on the roster so
    // the select doesn't hide legacy data.
    const rosterTypes = useMemo(() => {
        const s = new Set(RESOURCE_TYPES);
        for (const e of engineers) {
            const rt = e.resourceType || e.trade;
            if (rt) s.add(rt);
        }
        return Array.from(s);
    }, [engineers]);

    // Existing assignees that would conflict with the currently
    // selected requirement — flagged for the operator to resolve.
    const conflictingSelected = useMemo(() => {
        const req = (requirement || "").trim();
        if (!req) return [];
        return [...selected].filter((id) => {
            const e = engineers.find((x) => x.id === id);
            if (!e) return false;
            const rt = e.resourceType || e.trade || "";
            return rt !== req;
        });
    }, [selected, requirement, engineers]);

    // FIELD-RESOURCE-ASSIGNMENT-CORRECTION · Any currently-selected id
    // that is NOT on the active roster is "stale". Distinguish
    // archived (row exists but inactive) vs missing (row gone). Ops
    // must be able to safely remove either without saving them back
    // to the server. Save is blocked until every stale id is removed
    // so the backend never receives a raw "Unknown or archived
    // assignees: <uuid>" error.
    const staleSelected = useMemo(() => {
        if (loading) return [];
        const activeIds = new Set(engineers.map((e) => e.id));
        return [...selected]
            .filter((id) => !activeIds.has(id))
            .map((id) => {
                const arch = archivedById[id];
                if (arch) {
                    return {
                        id,
                        reason: "archived",
                        label: arch.displayName || arch.organisationName || id,
                        resourceType: arch.resourceType || arch.trade || null,
                    };
                }
                return {
                    id,
                    reason: "missing",
                    label: `Unknown resource (id: ${String(id).slice(0, 8)}…)`,
                    resourceType: null,
                };
            });
    }, [selected, engineers, archivedById, loading]);

    const removeStale = (id) => {
        setSelected((prev) => {
            const next = new Set(prev);
            next.delete(id);
            return next;
        });
    };

    const clearAllStale = () => {
        setSelected((prev) => {
            const next = new Set(prev);
            for (const s of staleSelected) next.delete(s.id);
            return next;
        });
    };

    const nothingChanged = useMemo(() => {
        const before = [...currentAssigneeIds].sort();
        const after = [...selected].sort();
        if (before.length !== after.length) return false;
        if (!before.every((v, i) => v === after[i])) return false;
        // Requirement change alone should enable Save.
        return !requirementDirty || (requirement || "") === (currentRequirement || "");
    }, [currentAssigneeIds, selected, requirement, requirementDirty, currentRequirement]);

    const cannotSave = saving
        || nothingChanged
        || conflictingSelected.length > 0
        || staleSelected.length > 0;

    const save = async () => {
        setSaving(true);
        setError("");
        try {
            const ids = [...selected];
            const opts = {};
            if (requirementDirty) {
                opts.requiredResourceType = requirement || "";
            }
            const updated = await api.updateTaskAssignees(projectId, task.id, ids, opts);
            toast.success(ids.length === 0
                ? "Task assignment cleared"
                : `Task assigned to ${ids.length} ${ids.length === 1 ? "resource" : "resources"}`);
            onSaved?.(updated);
        } catch (e) {
            const detail = e?.response?.data?.detail;
            const msg = (typeof detail === "object" ? detail?.message : null)
                     || formatApiError(detail)
                     || "Could not save assignment";
            setError(msg);
            toast.error(msg);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open onOpenChange={(v) => !v && !saving && onClose?.()}>
            <DialogContent
                className="max-w-md flex flex-col max-h-[85vh]"
                data-testid="ops-task-assignees-dialog"
            >
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-[#131B2F]"
                                 data-testid="ops-task-assignees-title">
                        <Users size={16} strokeWidth={1.75} className="text-[#3E9E2E]" />
                        {Array.isArray(currentAssigneeIds) && currentAssigneeIds.length > 0
                            ? "Reassign task"
                            : "Assign task"}
                    </DialogTitle>
                    <div className="text-xs text-[#4B5565] mt-1" data-testid="ops-task-assignees-task-name">
                        {task?.name}
                    </div>
                </DialogHeader>

                {/* FIELD-001C2A · Required resource type — separate from the
                    actual assignees. A requirement of e.g. "Scaffolder" does
                    NOT auto-assign anyone; it constrains what Operations may
                    select AND is enforced server-side. */}
                <div className="mt-2">
                    <label htmlFor="ops-task-required-type"
                           className="block text-[11px] text-[#4B5565] mb-1">
                        Required resource type
                    </label>
                    <select
                        id="ops-task-required-type"
                        value={requirement}
                        onChange={(e) => { setRequirement(e.target.value); setRequirementDirty(true); }}
                        disabled={saving || loading}
                        data-testid="ops-task-required-type"
                        className="w-full h-9 rounded-md border border-[#E5E7EB] bg-white px-2 text-sm text-[#131B2F] focus:outline-none focus:ring-2 focus:ring-[#131B2F]/10 focus:border-[#131B2F] disabled:opacity-60"
                    >
                        <option value="">Any resource type</option>
                        {rosterTypes.map((t) => (
                            <option key={t} value={t}>{t}</option>
                        ))}
                    </select>
                    <div className="text-[11px] text-[#4B5565] mt-1">
                        Setting a type filters the picker to matching resources; it does not assign anyone.
                    </div>
                </div>

                <div className="relative mt-3">
                    <Search size={14} strokeWidth={1.75}
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-[#4B5565]" />
                    <input
                        type="text"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search by name, type, kind or email"
                        data-testid="ops-task-assignees-search"
                        className="w-full h-9 pl-9 pr-3 rounded-md border border-[#E5E7EB] bg-white text-sm text-[#131B2F] focus:outline-none focus:ring-2 focus:ring-[#131B2F]/10 focus:border-[#131B2F]"
                    />
                </div>

                <div className="mt-2 flex items-center justify-between">
                    <div className="text-xs text-[#4B5565]" data-testid="ops-task-assignees-count">
                        {selected.size === 0
                            ? "No resources selected (unassigned)."
                            : `${selected.size} selected`}
                    </div>
                    {selected.size > 0 && (
                        <button
                            type="button"
                            onClick={clearAll}
                            disabled={saving}
                            data-testid="ops-task-assignees-clear"
                            className="text-xs text-[#DA3535] hover:text-[#B62828] transition-colors"
                        >
                            Clear all
                        </button>
                    )}
                </div>

                {conflictingSelected.length > 0 && (
                    <div className="mt-2 flex items-start gap-2 rounded-md border border-[#F5A524]/40 bg-[#FEF3C7] p-2 text-[11px] text-[#92400E]"
                         data-testid="ops-task-assignees-conflict">
                        <AlertTriangle size={12} strokeWidth={2} className="mt-0.5 shrink-0" />
                        <div>
                            Selected resources don’t match the required type — resolve before saving.
                            Existing assignees are never silently reassigned.
                        </div>
                    </div>
                )}

                {/* FIELD-RESOURCE-ASSIGNMENT-CORRECTION · Current stale
                    assignees rendered above the roster so Operations
                    sees the failure state before Save can fire. Each
                    row can be removed individually or in one click via
                    "Remove all". Save is disabled while any stale row
                    is still selected, so the server never receives an
                    "Unknown or archived assignees" 400. */}
                {staleSelected.length > 0 && (
                    <div className="mt-2 rounded-md border border-[#F5A524]/40 bg-[#FEF3C7] p-2 text-[11px] text-[#92400E]"
                         data-testid="ops-task-assignees-stale-banner">
                        <div className="flex items-start justify-between gap-2">
                            <div className="flex items-start gap-2">
                                <AlertTriangle size={12} strokeWidth={2} className="mt-0.5 shrink-0" />
                                <div data-testid="ops-task-assignees-stale-message">
                                    {staleSelected.length === 1
                                        ? "1 current assignee is archived or unknown. Remove it to save."
                                        : `${staleSelected.length} current assignees are archived or unknown. Remove them to save.`}
                                </div>
                            </div>
                            <button
                                type="button"
                                onClick={clearAllStale}
                                disabled={saving}
                                data-testid="ops-task-assignees-stale-clear-all"
                                className="text-[11px] font-medium underline underline-offset-2 hover:text-[#7C2D12] disabled:opacity-60"
                            >
                                Remove all
                            </button>
                        </div>
                        <ul className="mt-1.5 space-y-1">
                            {staleSelected.map((s) => (
                                <li key={s.id}
                                    data-testid={`ops-task-assignees-stale-row-${s.id}`}
                                    data-stale-reason={s.reason}
                                    className="flex items-center justify-between gap-2 bg-white/60 rounded px-2 py-1">
                                    <div className="min-w-0 truncate">
                                        <span className="text-[#131B2F] font-medium">{s.label}</span>
                                        <span className="ml-1 px-1 rounded bg-[#FDE68A] text-[10px] uppercase tracking-wide">
                                            {s.reason}
                                        </span>
                                        {s.resourceType && (
                                            <span className="ml-1 text-[10px] text-[#7C2D12]/80">
                                                · {s.resourceType}
                                            </span>
                                        )}
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => removeStale(s.id)}
                                        disabled={saving}
                                        data-testid={`ops-task-assignees-stale-remove-${s.id}`}
                                        aria-label={`Remove ${s.label} from selection`}
                                        className="text-[11px] font-medium text-[#B45309] hover:text-[#7C2D12] disabled:opacity-60"
                                    >
                                        Remove
                                    </button>
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                <div
                    className="mt-2 border border-[#E5E7EB] rounded-md divide-y divide-[#F0F1F5] overflow-y-auto flex-1"
                    data-testid="ops-task-assignees-list"
                >
                    {loading ? (
                        <div className="p-6 text-center text-xs text-[#4B5565]">
                            <Loader2 size={14} className="inline animate-spin" /> Loading resources…
                        </div>
                    ) : filtered.length === 0 ? (
                        <div className="p-6 text-center text-xs text-[#4B5565]"
                             data-testid="ops-task-assignees-empty">
                            No active resources match.
                        </div>
                    ) : (
                        filtered.map((e) => {
                            const on = selected.has(e.id);
                            const rt = e.resourceType || e.trade || "";
                            const kind = e.kind || "internal_person";
                            const req = (requirement || "").trim();
                            const mismatched = !!req && rt !== req;
                            const disabled = mismatched && !on;
                            const label = e.displayName || e.organisationName || e.id;
                            return (
                                <button
                                    key={e.id}
                                    type="button"
                                    onClick={() => toggle(e.id, disabled)}
                                    disabled={saving || disabled}
                                    data-testid={`ops-task-assignees-opt-${e.id}`}
                                    data-mismatched={mismatched ? "true" : undefined}
                                    aria-pressed={on}
                                    className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${
                                        on ? "bg-[#EAF6E1]"
                                           : disabled ? "opacity-50"
                                           : "hover:bg-[#F5F5F7]"
                                    } disabled:cursor-not-allowed`}
                                >
                                    <span
                                        className={`w-5 h-5 rounded border flex items-center justify-center shrink-0 ${
                                            on ? "bg-[#3E9E2E] border-[#3E9E2E] text-white"
                                               : "bg-white border-[#D1D5DB]"
                                        }`}
                                    >
                                        {on && <Check size={12} strokeWidth={2.5} />}
                                    </span>
                                    <span
                                        className="w-7 h-7 rounded-full flex items-center justify-center text-white text-[10px] font-medium shrink-0"
                                        style={{ background: e.colour || "#131B2F" }}
                                    >
                                        {e.initials || (label || "?").slice(0, 2).toUpperCase()}
                                    </span>
                                    <div className="flex-1 min-w-0">
                                        <div className="text-sm text-[#131B2F] font-medium truncate">
                                            {label}
                                        </div>
                                        <div className="text-[11px] text-[#4B5565] truncate flex items-center gap-1.5">
                                            <span data-testid={`ops-task-assignees-kind-${e.id}`}>
                                                {KIND_LABEL[kind] || "Internal"}
                                            </span>
                                            <span>·</span>
                                            <span data-testid={`ops-task-assignees-type-${e.id}`}>
                                                {rt || "—"}
                                            </span>
                                            {mismatched && (
                                                <span data-testid={`ops-task-assignees-mismatch-${e.id}`}
                                                      className="ml-1 px-1 rounded bg-[#FEF3C7] text-[#92400E]">
                                                    Type mismatch
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                </button>
                            );
                        })
                    )}
                </div>

                {error && (
                    <div className="mt-2 text-xs text-[#DA3535]" data-testid="ops-task-assignees-error">
                        {error}
                    </div>
                )}

                <DialogFooter className="mt-3">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={saving}
                        data-testid="ops-task-assignees-cancel"
                        className="pe-btn-outline h-10 disabled:opacity-60"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={save}
                        disabled={cannotSave}
                        data-testid="ops-task-assignees-save"
                        className="pe-btn-primary h-10 disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                        {saving ? (<><Loader2 size={14} className="animate-spin" /> Saving…</>) : "Save assignment"}
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
